import React, {useState, useMemo, useRef, useEffect} from 'react';
import {Dialog} from "primereact/dialog";
import {stashWizardResume} from '../../utils/amazonOAuth';
import {Button} from "primereact/button";
import {Message} from "primereact/message";

import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import {CampaignService} from "../../service/CampaignService";
import {wizardStepKeys} from "../Onboarding/campaignWizardSteps";
import WizardGuide from "../Onboarding/WizardGuide";
import SearchMatchInfoStep from "../Onboarding/SearchMatchInfoStep";
import ProductsOnboardingStep from "../Onboarding/ProductsOnboardingStep";
import SitesOnboardingStep from "../Onboarding/SitesOnboardingStep";
import PriceCheckOnboardingStep from "../Onboarding/PriceCheckOnboardingStep";
import PricingOnboardingStep from "../Onboarding/PricingOnboardingStep";
import MapOnboardingStep from "../Onboarding/MapOnboardingStep";
import MarketplaceConnectStep from "../Onboarding/MarketplaceConnectStep";
import MarketplaceProductsStep from "../Onboarding/MarketplaceProductsStep";
import CampaignEdit from "./CampaignEdit";

// "Add campaign" kurulum sihirbazı. Steps bar YOK — yalnızca adım içeriği,
// başlıkta "Adım X/Y" metni ve İleri/Geri/Bitir.
const CampaignWizard = ({visible, onHide, campaigns, onSaved, resume}) => {
    const {t, setError} = React.useContext(AppContext);
    const {selectCampaign} = React.useContext(CampaignContext);
    const campaignService = useMemo(() => new CampaignService(), []);

    const [stepIndex, setStepIndex] = useState(0);
    const [draft, setDraft] = useState(null);        // CampaignEdit'ten gelen basics draft
    const [createdId, setCreatedId] = useState(null); // mükerrer oluşturmayı önler
    const [usageType, setUsageType] = useState(null); // adım listesini belirler
    const [marketplaceApi, setMarketplaceApi] = useState(null); // presence of settings.marketplace_api (boolean), also drives step list
    const [saving, setSaving] = useState(false);
    const [nameError, setNameError] = useState(null);
    const priceCheckRef = useRef(null);   // price_check adımını ref ile kaydetmek için
    const connectRef = useRef(null);      // saves the mp_connect step via its ref

    const stepKeys = wizardStepKeys(usageType, marketplaceApi);
    const stepKey = stepKeys[stepIndex];
    const isFirst = stepIndex === 0;
    const isLast = stepIndex === stepKeys.length - 1;

    const reset = () => {
        setStepIndex(0); setDraft(null); setCreatedId(null);
        setUsageType(null); setMarketplaceApi(null); setSaving(false); setNameError(null);
    };

    const close = () => { reset(); onHide(); };

    // Reopen where the seller left off after the Amazon consent round trip.
    // Only the campaign id and the two step-list inputs are carried across
    // (see stashWizardResume); the step index itself is re-derived here rather
    // than stored, because which steps exist depends on those inputs and a
    // stale index would land on the wrong one.
    useEffect(() => {
        if (!visible || !resume || !resume.campaignId) return;
        const usage = resume.usageType || null;
        const mpApi = !!resume.marketplaceApi;
        setUsageType(usage);
        setMarketplaceApi(mpApi);
        setCreatedId(resume.campaignId);
        const keys = wizardStepKeys(usage, mpApi);
        const index = keys.indexOf('mp_connect');
        // Select the campaign first, the same way persistBasics does, so the
        // step renders against the right campaign context rather than whatever
        // was selected before the browser left.
        selectCampaign(resume.campaignId).then(() => setStepIndex(index >= 0 ? index : 0));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, resume]);

    // Adım 1'den ileri: kampanyayı oluştur (veya güncelle) ve context'e set et.
    const persistBasics = () => {
        const name = (draft?.name || '').trim();
        if (!name) { setNameError(t('validations.invalid_name')); return; }
        setNameError(null);
        setSaving(true);
        const ut = draft.settings?.usage_type;
        const settings = draft.settings;
        // Presence-based: an abandoned-then-resumed Buybox card leaves
        // settings.marketplace_api = {} (marketplace not chosen yet) until the
        // connect step writes {marketplace: m} into it -- that empty object must
        // still count as "this is a marketplace API campaign" for the step list.
        const mpApi = !!settings?.marketplace_api;
        const done = (saved) => {
            setSaving(false);
            setCreatedId(saved.id);
            setUsageType(ut);
            setMarketplaceApi(mpApi);
            if (onSaved) onSaved(saved);
            selectCampaign(saved.id).then(() => setStepIndex(1));
        };
        const fail = (error) => { setSaving(false); setError(error); setError(null); };

        if (createdId == null) {
            campaignService.addCampaign(name, draft.use_sale_value, draft.is_parent, draft.parent_campaign, settings)
                .then(saved => { saved.scraping_requests = []; done(saved); })
                .catch(fail);
        } else {
            campaignService.editCampaign(createdId, name, draft.use_sale_value, draft.is_parent, draft.parent_campaign, settings)
                .then(() => done({...draft, id: createdId}))
                .catch(fail);
        }
    };

    const advance = () => setStepIndex(i => Math.min(stepKeys.length - 1, i + 1));

    // "Next" on price_check / mp_connect → call the step's own save() via ref;
    // advance on success (save() resolving false keeps the wizard on the step; it never rejects).
    const persistViaRef = (ref) => {
        const api = ref.current;
        if (!api || !api.save) { advance(); return; }
        setSaving(true);
        Promise.resolve(api.save())
            .then((ok) => { setSaving(false); if (ok !== false) advance(); })
            .catch(() => setSaving(false));
    };

    const goNext = () => {
        if (isFirst) { persistBasics(); return; }
        if (stepKey === 'price_check') { persistViaRef(priceCheckRef); return; }
        if (stepKey === 'mp_connect') { persistViaRef(connectRef); return; }
        if (isLast) { close(); return; }   // onHide (Campaign.js) refreshes the list
        advance();
    };
    const goBack = () => setStepIndex(i => Math.max(0, i - 1));

    const renderStep = () => {
        if (stepKey === 'basics')
            return (
                <div>
                    <WizardGuide icon="pi pi-flag" color="#2563eb" bg="#eff6ff"
                                 title={t('wizard.basics.title')}>
                        {t('wizard.basics.guide')}
                    </WizardGuide>
                    <CampaignEdit mode="wizard" page="add" campaigns={campaigns} onDraftChange={setDraft}/>
                </div>
            );
        if (stepKey === 'products')
            return (
                <div>
                    <WizardGuide icon="pi pi-box" color="#7c3aed" bg="#f5f3ff"
                                 title={t('wizard.products.title')}>
                        {t('wizard.products.guide')}
                    </WizardGuide>
                    <ProductsOnboardingStep t={t} status={null} refresh={() => {}} wizardMode/>
                </div>
            );
        if (stepKey === 'sites')
            return (
                <div>
                    <WizardGuide icon="pi pi-globe" color="#0891b2" bg="#ecfeff"
                                 title={t('wizard.sites.title')}>
                        {t('wizard.sites.guide')}
                    </WizardGuide>
                    <SitesOnboardingStep t={t} status={null} refresh={() => {}} wizardMode/>
                </div>
            );
        if (stepKey === 'price_check')
            return (
                <div>
                    <WizardGuide icon="pi pi-clock" color="#d97706" bg="#fffbeb"
                                 title={t('wizard.priceCheck.title')}>
                        {t('wizard.priceCheck.guide')}
                    </WizardGuide>
                    <PriceCheckOnboardingStep ref={priceCheckRef} t={t} status={null} refresh={() => {}} wizardMode/>
                </div>
            );
        if (stepKey === 'mp_connect')
            return (
                <div>
                    <WizardGuide icon="pi pi-link" color="#f59e0b" bg="#fffbeb"
                                 title={t('wizard.mpConnect.title')}>
                        {t('wizard.mpConnect.guide')}
                    </WizardGuide>
                    <MarketplaceConnectStep t={t} campaignId={createdId} ref={connectRef}
                                            onBeforeRedirect={() => stashWizardResume({
                                                campaignId: createdId, usageType, marketplaceApi})}/>
                </div>
            );
        if (stepKey === 'mp_products')
            return (
                <div>
                    <WizardGuide icon="pi pi-box" color="#7c3aed" bg="#f5f3ff"
                                 title={t('wizard.mpProducts.title')}>
                        {t('wizard.mpProducts.guide')}
                    </WizardGuide>
                    <MarketplaceProductsStep t={t} campaignId={createdId}/>
                </div>
            );
        if (stepKey === 'pricing')
            return (
                <div>
                    <WizardGuide icon="pi pi-dollar" color="#16a34a" bg="#f0fdf4"
                                 title={t('wizard.pricing.title')}>
                        {t('wizard.pricing.guide')}
                    </WizardGuide>
                    <PricingOnboardingStep t={t} refresh={() => {}} embedded wizardMode/>
                </div>
            );
        if (stepKey === 'map')
            return (
                <div>
                    <WizardGuide icon="pi pi-shield" color="#dc2626" bg="#fef2f2"
                                 title={t('wizard.map.title')}>
                        {t('wizard.map.guide')}
                    </WizardGuide>
                    <MapOnboardingStep t={t} embedded/>
                </div>
            );
        if (stepKey === 'search_match')
            return <SearchMatchInfoStep t={t}/>;
        return null;
    };

    const header = (
        <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
            <span style={{fontWeight: 800, fontSize: '1.1rem'}}>{t('wizard.title')}</span>
            <span style={{fontSize: '.8rem', color: '#6b7280', fontWeight: 600}}>
                {t('wizard.stepCounter').replace('{x}', stepIndex + 1).replace('{y}', stepKeys.length)}
            </span>
        </div>
    );

    const footer = (
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <Button label={t('onboarding.nav.back')} icon="pi pi-chevron-left" className="p-button-text"
                    onClick={goBack} disabled={isFirst || saving}/>
            <Button label={isLast ? t('onboarding.nav.finish') : t('onboarding.nav.next')}
                    icon={isLast ? 'pi pi-check' : 'pi pi-chevron-right'} iconPos="right"
                    loading={saving} onClick={goNext}/>
        </div>
    );

    return (
        <Dialog header={header} footer={footer} visible={visible} modal
                style={{width: '1040px', maxWidth: '96vw'}} contentStyle={{minHeight: '60vh'}}
                onHide={close}>
            {nameError && isFirst && <div style={{marginBottom: 12}}><Message severity="error" text={nameError}/></div>}
            {renderStep()}
        </Dialog>
    );
};

export default CampaignWizard;
